import { Badge, Button, Input, Modal, Select, Spinner } from '@/components/ui';
import { useAsesores, useCombinaciones } from '@/hooks/useSysadmin';
import type { CreateEmpresaData, CreateEmpresaResponse } from '@/services/sysadmin.service';
import { AlertTriangle, ArrowLeft, ArrowRight, Building2, CheckCircle2, Mail, MapPin, User } from 'lucide-react';
import { useState } from 'react';

interface CrearEmpresaWizardProps {
  isOpen: boolean;
  onClose: () => void;
  crearEmpresa: (data: CreateEmpresaData) => Promise<CreateEmpresaResponse>;
}

type Step = 1 | 2 | 3;

interface FormData {
  nitId: string;
  nombre: string;
  departamento: string;
  municipio: string;
  ubicacion: string;
  correoEmpresa: string;
  representanteLegal: string;
  rnt: string;
  rntCategoria: string;
  rntSubCategoria: string;
  combinacionNtcId: string;
  asesorId: string;
}

const initialFormData: FormData = {
  nitId: '',
  nombre: '',
  departamento: '',
  municipio: '',
  ubicacion: '',
  correoEmpresa: '',
  representanteLegal: '',
  rnt: '',
  rntCategoria: '',
  rntSubCategoria: '',
  combinacionNtcId: '',
  asesorId: '',
};

export function CrearEmpresaWizard({ isOpen, onClose, crearEmpresa }: CrearEmpresaWizardProps) {
  const { asesores, isLoading: loadingAsesores } = useAsesores();
  const { combinaciones, isLoading: loadingCombinaciones } = useCombinaciones();

  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<CreateEmpresaResponse | null>(null);

  const updateField = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    setStep(1);
    setForm(initialFormData);
    setError(null);
    setSuccess(null);
    onClose();
  };

  // --- Validación por paso ---

  const isStep1Valid = () => form.nitId.trim() !== '' && form.nombre.trim() !== '';

  const isStep2Valid = () =>
    form.departamento.trim() !== '' &&
    form.municipio.trim() !== '' &&
    form.correoEmpresa.trim() !== '' &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correoEmpresa);

  const canProceed = () => {
    if (step === 1) return isStep1Valid();
    if (step === 2) return isStep2Valid();
    return true;
  };

  // --- Submit ---

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const data: CreateEmpresaData = {
        nitId: form.nitId.trim(),
        nombre: form.nombre.trim(),
        departamento: form.departamento.trim(),
        municipio: form.municipio.trim(),
        correoEmpresa: form.correoEmpresa.trim(),
      };
      if (form.ubicacion.trim()) data.ubicacion = form.ubicacion.trim();
      if (form.representanteLegal.trim()) data.representanteLegal = form.representanteLegal.trim();
      if (form.rnt.trim()) data.rnt = form.rnt.trim();
      if (form.rntCategoria.trim()) data.rntCategoria = form.rntCategoria.trim();
      if (form.rntSubCategoria.trim()) data.rntSubCategoria = form.rntSubCategoria.trim();
      if (form.combinacionNtcId) data.combinacionNtcId = parseInt(form.combinacionNtcId);
      if (form.asesorId) data.asesorId = parseInt(form.asesorId);

      const res = await crearEmpresa(data);
      setSuccess(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear empresa');
    } finally {
      setSaving(false);
    }
  };

  // --- Render por paso ---

  const selectedCombinacion = form.combinacionNtcId
    ? combinaciones.find((c) => c.combinacionId.toString() === form.combinacionNtcId)
    : null;

  const renderStepIndicator = () => (
    <div className="flex items-center gap-2 mb-6">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              s === step ? 'bg-blue-600 text-white' : s < step ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
            }`}
          >
            {s < step ? '✓' : s}
          </div>
          {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-green-300' : 'bg-gray-200'}`} />}
        </div>
      ))}
      <span className="ml-2 text-sm text-gray-500">
        {step === 1 && 'Datos generales'}
        {step === 2 && 'Datos de detalle'}
        {step === 3 && 'Asignación NTC y Asesor'}
      </span>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          NIT <span className="text-red-500">*</span>
        </label>
        <Input placeholder="Ej: 900123456-7" value={form.nitId} onChange={(e) => updateField('nitId', e.target.value)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nombre de la empresa <span className="text-red-500">*</span>
        </label>
        <Input placeholder="Ej: Hotel Mirador del Café" value={form.nombre} onChange={(e) => updateField('nombre', e.target.value)} />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Departamento <span className="text-red-500">*</span>
          </label>
          <Input placeholder="Ej: Quindío" value={form.departamento} onChange={(e) => updateField('departamento', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Municipio <span className="text-red-500">*</span>
          </label>
          <Input placeholder="Ej: Armenia" value={form.municipio} onChange={(e) => updateField('municipio', e.target.value)} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
        <Input placeholder="Dirección (opcional)" value={form.ubicacion} onChange={(e) => updateField('ubicacion', e.target.value)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Correo de la empresa <span className="text-red-500">*</span>
        </label>
        <Input
          type="email"
          placeholder="empresa@ejemplo.com"
          value={form.correoEmpresa}
          onChange={(e) => updateField('correoEmpresa', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Representante Legal</label>
        <Input
          placeholder="Nombre completo (opcional)"
          value={form.representanteLegal}
          onChange={(e) => updateField('representanteLegal', e.target.value)}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">RNT</label>
          <Input placeholder="Opcional" value={form.rnt} onChange={(e) => updateField('rnt', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Categoría RNT</label>
          <Input placeholder="Opcional" value={form.rntCategoria} onChange={(e) => updateField('rntCategoria', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subcategoría RNT</label>
          <Input placeholder="Opcional" value={form.rntSubCategoria} onChange={(e) => updateField('rntSubCategoria', e.target.value)} />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Opcionalmente, asigna una combinación de NTCs y un asesor a la empresa. Puedes hacerlo después desde la tabla de empresas.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Combinación de NTCs</label>
        {loadingCombinaciones ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Cargando combinaciones...
          </div>
        ) : (
          <Select
            value={form.combinacionNtcId}
            onChange={(e) => updateField('combinacionNtcId', e.target.value)}
            options={[
              { value: '', label: '— Sin combinación —' },
              ...combinaciones.map((c) => ({
                value: c.combinacionId.toString(),
                label: c.nombre,
              })),
            ]}
          />
        )}
      </div>

      {/* Detalle de la combinación seleccionada */}
      {selectedCombinacion && (
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs font-medium text-blue-800 uppercase mb-2">NTCs incluidas</p>
          <div className="space-y-1">
            {selectedCombinacion.ntcs.map((ntc) => (
              <div key={ntc.ntcId} className="flex justify-between items-center text-sm">
                <span className="text-blue-900">{ntc.nombre}</span>
                <Badge variant="blue">{ntc.totalRequisitos} requisitos</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Asesor</label>
        {loadingAsesores ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Cargando asesores...
          </div>
        ) : (
          <Select
            value={form.asesorId}
            onChange={(e) => updateField('asesorId', e.target.value)}
            options={[
              { value: '', label: '— Sin asesor —' },
              ...asesores
                .filter((a) => a.estado && a.rol === 'ASESOR')
                .map((a) => ({
                  value: a.asesorId.toString(),
                  label: `${a.nombre} (${a.cantidadEmpresas} empresas)`,
                })),
            ]}
          />
        )}
      </div>
    </div>
  );

  // --- Pantalla de éxito ---
  if (success) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Empresa Creada" size="md">
        <div className="space-y-4 text-center py-4">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{success.empresa.nombre}</h3>
            <p className="text-sm text-gray-500">NIT: {success.empresa.nitId}</p>
          </div>
          <p className="text-sm text-gray-600">{success.message}</p>
          {success.empresa.requisitosCreados > 0 && <Badge variant="green">{success.empresa.requisitosCreados} requisitos creados</Badge>}
          <div className="pt-2">
            <Button onClick={handleClose}>Cerrar</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Nueva Empresa" size="lg">
      <div className="space-y-4">
        {renderStepIndicator()}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}

        {/* Resumen antes de crear */}
        {step === 3 && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase">Resumen</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700">{form.nombre || '—'}</span>
              </div>
              <div className="text-gray-500">NIT: {form.nitId || '—'}</div>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700">
                  {form.municipio || '—'}, {form.departamento || '—'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700">{form.correoEmpresa || '—'}</span>
              </div>
              {form.representanteLegal && (
                <div className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-gray-700">{form.representanteLegal}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navegación */}
        <div className="flex justify-between pt-2">
          <div>
            {step > 1 && (
              <Button variant="secondary" onClick={() => setStep((s) => (s - 1) as Step)}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            {step < 3 ? (
              <Button onClick={() => setStep((s) => (s + 1) as Step)} disabled={!canProceed()}>
                Siguiente
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={saving || !isStep1Valid() || !isStep2Valid()}>
                {saving ? 'Creando...' : 'Crear Empresa'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
